import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { StagedFile, GroupedAsset, GroupingResult, GroupType } from '@/types/assetGrouping';

export function useAssetGrouping() {
  const [conventions, setConventions] = useState<any[]>([]);
  const [selectedConventionId, setSelectedConventionId] = useState<string | null>(null);
  const [groupType, setGroupType] = useState<'pair' | 'triple'>('pair');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchConventions();
  }, []);

  const fetchConventions = async () => {
    setLoading(true);
    const { data: { user } } = await supabase.auth.getUser();
    
    const { data, error } = await supabase
      .from('naming_conventions')
      .select('*')
      .or(`user_id.is.null,user_id.eq.${user?.id}`)
      .order('user_id', { ascending: true, nullsFirst: true })
      .order('label');

    if (!error && data) {
      setConventions(data);
      // Auto-select first matching convention
      const filtered = data.filter(c => 
        groupType === 'pair' ? !c.landscape_suffix : !!c.landscape_suffix
      );
      if (filtered.length > 0 && !selectedConventionId) {
        setSelectedConventionId(filtered[0].id);
      }
    }
    setLoading(false);
  };

  const getFilteredConventions = () => {
    return conventions.filter(c => 
      groupType === 'pair' ? !c.landscape_suffix : !!c.landscape_suffix
    );
  };

  const groupFiles = (files: StagedFile[]): GroupingResult => {
    const selectedFiles = files.filter(f => f.selected);
    const convention = conventions.find(c => c.id === selectedConventionId);
    
    if (!convention || selectedFiles.length === 0) {
      return { grouped: [], unmatched: selectedFiles };
    }

    const { square_suffix, vertical_suffix, landscape_suffix } = convention;
    const grouped: GroupedAsset[] = [];
    const matched = new Set<string>();

    // Compute a group key by dynamically stripping the convention suffix
    const allSuffixes = [square_suffix, vertical_suffix, landscape_suffix].filter(Boolean) as string[];

    const computeGroupKey = (filename: string): string => {
      let name = filename.replace(/\.[^/.]+$/, '');
      for (const suffix of allSuffixes) {
        const escaped = suffix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(`[_ \\-]${escaped}(?=[_ .\\-]|$)`, 'i');
        const match = name.match(regex);
        if (match) {
          name = name.slice(0, match.index) + name.slice(match.index! + match[0].length);
          break;
        }
      }
      name = name.replace(/^[_ \-]+|[_ \-]+$/g, '').replace(/[_ \-]{2,}/g, '_');
      return name;
    };

    // Detect which role a file has based on which suffix it contains
    const detectRole = (filename: string): 'square' | 'vertical' | 'landscape' | null => {
      const name = filename.replace(/\.[^/.]+$/, '');
      const check = (suffix: string) => {
        const escaped = suffix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        return new RegExp(`(?:^|[_ \\-])${escaped}(?=[_ .\\-]|$)`, 'i').test(name);
      };
      if (square_suffix && check(square_suffix)) return 'square';
      if (vertical_suffix && check(vertical_suffix)) return 'vertical';
      if (landscape_suffix && check(landscape_suffix)) return 'landscape';
      return null;
    };

    // Build a map of groupKey -> files by role
    const groupMap = new Map<string, Map<string, StagedFile>>();
    selectedFiles.forEach(file => {
      const role = detectRole(file.name);
      if (!role) return;
      const key = computeGroupKey(file.name).toLowerCase();
      if (!groupMap.has(key)) groupMap.set(key, new Map());
      const roleMap = groupMap.get(key)!;
      if (!roleMap.has(role)) roleMap.set(role, file);
    });

    // Convert map to grouped results
    groupMap.forEach((roleMap, key) => {
      const squareFile = roleMap.get('square');
      const verticalFile = roleMap.get('vertical');
      if (!squareFile || !verticalFile) return;

      if (groupType === 'triple') {
        const landscapeFile = roleMap.get('landscape');
        if (!landscapeFile) return;
        matched.add(squareFile.id);
        matched.add(verticalFile.id);
        matched.add(landscapeFile.id);
        grouped.push({
          group_name: computeGroupKey(squareFile.name),
          group_type: groupType,
          assets: [
            { role: 'square', file: squareFile },
            { role: 'vertical', file: verticalFile },
            { role: 'landscape', file: landscapeFile }
          ]
        });
      } else {
        matched.add(squareFile.id);
        matched.add(verticalFile.id);
        grouped.push({
          group_name: computeGroupKey(squareFile.name),
          group_type: groupType,
          assets: [
            { role: 'square', file: squareFile },
            { role: 'vertical', file: verticalFile }
          ]
        });
      }
    });

    const unmatched = selectedFiles.filter(f => !matched.has(f.id));
    return { grouped, unmatched };
  };

  return {
    conventions: getFilteredConventions(),
    allConventions: conventions,
    selectedConventionId,
    setSelectedConventionId,
    groupType,
    setGroupType,
    groupFiles,
    loading,
    refetchConventions: fetchConventions
  };
}
